import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatApiError, fmtDate, inr, localIsoDate } from "@/lib/utils";
import {
  Users2, Table2, Loader2, Plus, Trash2, Save, Check, X, ChevronLeft, ChevronRight,
  CalendarDays, LayoutGrid, Video, HardDrive, MessageCircle, Link2, UserPlus, Search, Crown,
} from "lucide-react";
import { toast } from "sonner";
import ShootCalendar from "@/components/ShootCalendar";
import DeliverablesPipeline from "@/components/DeliverablesPipeline";
import ShootCrew from "@/components/ShootCrew";
import HardDriveManager from "@/components/HardDriveManager";
import LeadManagement from "@/components/LeadManagement";
import PageHero from "@/components/PageHero";
import { useCapabilities } from "@/context/PermissionsContext";

// -------------- date helpers --------------
function ymd(d) { return localIsoDate(d); }
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
// Iter35 — payroll cycle is now Sunday → Saturday. `getDay()` returns 0=Sun..6=Sat,
// so subtract `getDay()` days to reach the enclosing Sunday.
function sundayOf(d) { const x = new Date(d); x.setDate(x.getDate() - x.getDay()); return x; }

const HOLIDAY = "HOLIDAY";

/**
 * /team workspace — Operations only (post-prod matrix + shoot calendar/crew + roster).
 *
 * Iter61: All money-side tooling (Weekly Payroll, Staff Ledger, Lifetime
 * Earnings, Office Cash Book) is decoupled to `/financials` — admin-only,
 * separate sidebar section.
 *
 *  Coordinator / staff  →  [ Matrix , Work Allocation , Shoot Calendar , Shoot Crew ]
 *  Admin                →  [ Matrix , Work Allocation , Shoot Calendar , Shoot Crew , Roster ]
 *
 * The backend still enforces `require_admin` on staff-rate writes; this
 * page just no longer renders their surfaces.
 */
export default function Team() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // iter175 — Roster tab visibility now inherits from the RBAC v2 `team`
  // module (Operations category) instead of a hardcoded role check.
  // Coordinators (and any custom role) granted Operations → Team access
  // now see the Roster tab. Full Control unlocks Add/Edit/Deactivate;
  // View Only still shows the roster read-only. Admin/Owner remain
  // unconditional via the backend resolver.
  const team = useCapabilities("team");
  const canManageRoster = team.canView;   // View Only or higher
  const canEditRoster = team.canEdit;     // Edit or higher (Add / rename / toggle)
  const canDeleteRoster = team.canDelete; // Full Control (Deactivate)
  const canWrite = user?.role !== "viewer";

  const tabs = useMemo(() => {
    const base = [
      { key: "matrix",              label: "Post-Prod Matrix",           icon: Table2 },
      { key: "allocation",          label: "Post-Prod Work Allocation",  icon: LayoutGrid },
      { key: "shoot-calendar",      label: "Shoot Calendar",             icon: CalendarDays },
      { key: "shoot-crew",          label: "Shoot Crew Management",      icon: Video },
      // iter108 — Hard Drive Manager is now a Team primary tab (moved from
      // the Post-Production sub-tab per user directive). Read-only vault
      // showing every physical drive with the couples archived on it.
      { key: "hard-drive-manager",  label: "Hard Drive Manager",         icon: HardDrive },
    ];
    if (isAdmin) {
      base.push({ key: "lead-management", label: "Lead Management", icon: Crown });
    }
    if (canManageRoster) {
      base.push({ key: "roster",   label: "Roster",              icon: Users2 });
    }
    return base;
  }, [canManageRoster, isAdmin]);
  // iter102 — Support deep-link into a specific tab (and, for shoot-crew,
  // pre-open a specific group's deliverables editor). Used by the Public
  // Client Tracker's admin "✎ Edit" pencil icon.
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = searchParams.get("tab") || "matrix";
  const [tab, setTab] = useState(initialTab);
  const editClient = searchParams.get("editClient") || "";
  const editPlace = searchParams.get("editPlace") || "";
  const openDeliverables = searchParams.get("openDeliverables") === "1";
  // Sync tab back to URL if user changes it manually so the "Edit" deep link
  // params stay coherent, and clear the edit query keys once consumed so
  // refreshing the page doesn't keep re-opening the editor.
  const handleTabChange = (nextTab) => {
    setTab(nextTab);
    const next = new URLSearchParams(searchParams);
    next.delete("editClient");
    next.delete("editPlace");
    next.delete("openDeliverables");
    if (nextTab === "matrix") next.delete("tab"); else next.set("tab", nextTab);
    setSearchParams(next, { replace: true });
  };

  return (
    <div className="space-y-6" data-testid="team-page">
      <PageHero
        eyebrow="Kahani Studio · Team & Ops"
        title={isAdmin ? "Matrix, Calendar & Crew" : "Daily Ops"}
        subtitle={isAdmin
          ? "Log daily work in the matrix, plan shoots from the calendar, and manage the shoot crew roster."
          : "Update the daily matrix and mark attendance from the shoot calendar."}
      />

      <div className="flex flex-wrap gap-1 border-b border-stone-200 -mb-px" data-testid="team-tabs">
        {tabs.map((t) => {
          const active = tab === t.key;
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => handleTabChange(t.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors ${
                active ? "text-rose-700 border-rose-700" : "text-stone-500 border-transparent hover:text-stone-800"
              }`}
              data-testid={`team-tab-${t.key}`}
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={1.8} /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === "matrix" && <MatrixTab canWrite={canWrite} />}
      {tab === "allocation" && (
        // iter185 — MONTH-scoped kanban (WorkAllocation) removed on
        // user request; the tab now hosts only the Deliverables
        // Pipeline. Underlying `/team/allocations/*` endpoints + data
        // are preserved.
        <DeliverablesPipeline canWrite={canWrite} />
      )}
      {tab === "shoot-calendar" && <ShootCalendar />}
      {tab === "hard-drive-manager" && <HardDriveManager />}
      {isAdmin && tab === "lead-management" && <LeadManagement />}
      {tab === "shoot-crew" && (
        <ShootCrew
          canWrite={canWrite}
          isAdmin={isAdmin}
          editClient={editClient}
          editPlace={editPlace}
          openDeliverables={openDeliverables}
        />
      )}
      {canManageRoster && tab === "roster" && (
        <RosterTab
          isAdmin={isAdmin}
          canEdit={canEditRoster}
          canDelete={canDeleteRoster}
        />
      )}
    </div>
  );
}

// ============================================================
// Post-Prod Matrix — Excel-style grid (write access for coord+ ; blinded from money)
// ============================================================
function MatrixTab({ canWrite }) {
  const [anchor, setAnchor] = useState(() => sundayOf(new Date()));
  const [days, setDays] = useState(7);
  const [staff, setStaff] = useState([]);
  const [cells, setCells] = useState({}); // { `${sid}:${date}`: {text, completed} }
  const [loading, setLoading] = useState(true);

  const dates = useMemo(() => {
    const out = [];
    for (let i = 0; i < days; i++) out.push(addDays(anchor, i));
    return out;
  }, [anchor, days]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const start = ymd(dates[0]);
      const end = ymd(dates[dates.length - 1]);
      const [{ data: allStaff }, { data: grid }] = await Promise.all([
        // iter117 — Post-Prod Matrix hides freelance crew (e.g., Shiva Rai).
        // They stay available in Shoot Crew + Calendar dropdowns via the
        // default `/team/staff` call from those surfaces.
        api.get("/team/staff?exclude_freelance=true"),
        api.get(`/team/matrix?start=${start}&end=${end}`),
      ]);
      setStaff(allStaff);
      const m = {};
      (grid.cells || []).forEach((c) => { m[`${c.staff_id}:${c.date}`] = { text: c.text || "", completed: !!c.completed }; });
      setCells(m);
    } finally { setLoading(false); }
  }, [dates]);
  useEffect(() => { load(); }, [load]);

  const updateCell = useCallback(async (sid, dateStr, patch) => {
    if (!canWrite) return;
    const key = `${sid}:${dateStr}`;
    setCells((c) => {
      const prev = c[key] || { text: "", completed: false };
      return { ...c, [key]: { ...prev, ...patch } };
    });
    const next = { ...(cells[key] || { text: "", completed: false }), ...patch };
    try {
      await api.post("/team/matrix", { staff_id: sid, date: dateStr, text: next.text || "", completed: !!next.completed });
    } catch {
      load();
    }
  }, [canWrite, cells, load]);

  const shiftWeek = (n) => setAnchor(addDays(anchor, 7 * n));

  return (
    <div className="space-y-3" data-testid="matrix-tab">
      <div className="card p-3 flex flex-wrap items-center gap-3">
        <button onClick={() => shiftWeek(-1)} className="btn btn-outline btn-sm" data-testid="matrix-prev"><ChevronLeft className="w-3.5 h-3.5" /></button>
        <div className="text-sm font-heading font-semibold text-stone-900" data-testid="matrix-range">
          {fmtDate(ymd(dates[0]))} — {fmtDate(ymd(dates[dates.length - 1]))}
        </div>
        <button onClick={() => shiftWeek(1)} className="btn btn-outline btn-sm" data-testid="matrix-next"><ChevronRight className="w-3.5 h-3.5" /></button>
        <div className="ml-auto inline-flex gap-1">
          {[7, 14, 30].map((n) => (
            <button key={n} onClick={() => setDays(n)}
              className={`text-xs px-2.5 py-1 rounded-full border ${days === n ? "bg-rose-50 text-rose-800 border-rose-200 font-semibold" : "text-stone-500 border-stone-200 hover:bg-stone-50"}`}
              data-testid={`matrix-range-${n}`}>{n} days</button>
          ))}
        </div>
      </div>

      <div className="text-[11px] text-stone-500">
        Type project text or <code className="bg-stone-100 px-1 py-0.5 rounded">NOTHING</code> → counted as PRESENT ·
        Type <code className="bg-stone-100 px-1 py-0.5 rounded">HOLIDAY</code> → unpaid ·
        Click ✓ to toggle the green &quot;Completed&quot; state.
      </div>

      {loading ? (
        <div className="card p-8 text-center text-stone-500 text-sm" data-testid="team-loading">
          <Loader2 className="w-4 h-4 mr-2 inline animate-spin" /> Loading grid…
        </div>
      ) : (
        <div className="card overflow-auto" style={{ maxHeight: "70vh" }}>
          <table className="min-w-full text-sm border-collapse" data-testid="matrix-grid">
            <thead className="sticky top-0 z-10">
              <tr className="bg-stone-100">
                <th className="sticky left-0 z-20 bg-stone-100 border-r border-stone-200 px-3 py-2 text-left text-[11px] uppercase tracking-wider text-stone-500 font-semibold min-w-[110px]">
                  Date
                </th>
                {staff.map((s) => (
                  <th key={s.id} className="border-r border-stone-200 px-3 py-2 text-left text-[11px] uppercase tracking-wider text-stone-500 font-semibold min-w-[200px]" data-testid={`matrix-col-${s.id}`}>
                    {s.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {dates.map((d) => {
                const ds = ymd(d);
                const wd = d.toLocaleDateString(undefined, { weekday: "short" });
                return (
                  <tr key={ds} className="align-top">
                    <td className="sticky left-0 bg-white border-r border-stone-200 px-3 py-2 whitespace-nowrap" data-testid={`matrix-row-${ds}`}>
                      <div className="font-mono text-xs text-stone-800">{ds}</div>
                      <div className="text-[10px] text-stone-400 uppercase tracking-wider">{wd}</div>
                    </td>
                    {staff.map((s) => (
                      <MatrixCell
                        key={`${s.id}:${ds}`}
                        sid={s.id}
                        dateStr={ds}
                        cell={cells[`${s.id}:${ds}`] || { text: "", completed: false }}
                        canWrite={canWrite}
                        onChange={updateCell}
                      />
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function MatrixCell({ sid, dateStr, cell, canWrite, onChange }) {
  const [text, setText] = useState(cell.text);
  const [completed, setCompleted] = useState(cell.completed);
  useEffect(() => { setText(cell.text); setCompleted(cell.completed); }, [cell.text, cell.completed]);

  const isHoliday = (text || "").trim().toUpperCase() === HOLIDAY;
  const isEmpty = !text.trim();

  const commit = () => {
    if (text === cell.text) return;
    onChange(sid, dateStr, { text });
  };
  const toggleComplete = () => {
    const next = !completed;
    setCompleted(next);
    onChange(sid, dateStr, { completed: next });
  };

  const bg = completed && !isHoliday && !isEmpty
    ? "bg-emerald-100"
    : isHoliday ? "bg-amber-50"
    : "bg-white hover:bg-stone-50";

  return (
    <td className={`border-r border-stone-100 p-1 ${bg} transition-colors`} data-testid={`matrix-cell-${sid}-${dateStr}`}>
      <div className="flex items-start gap-1">
        <textarea
          rows={2}
          value={text}
          disabled={!canWrite}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          className={`w-full text-xs resize-none bg-transparent focus:outline-none focus:ring-1 focus:ring-rose-400 rounded px-1 py-0.5 ${
            isHoliday ? "text-amber-800 font-semibold uppercase tracking-wider" : "text-stone-800"
          }`}
          placeholder="—"
          data-testid={`matrix-input-${sid}-${dateStr}`}
        />
        {canWrite && !isEmpty && !isHoliday && (
          <button
            type="button"
            onClick={toggleComplete}
            title={completed ? "Mark not-completed" : "Mark completed"}
            className={`shrink-0 w-5 h-5 mt-0.5 rounded flex items-center justify-center border transition-colors ${
              completed ? "bg-emerald-600 border-emerald-600 text-white" : "bg-white border-stone-300 text-stone-400 hover:border-emerald-500"
            }`}
            data-testid={`matrix-complete-${sid}-${dateStr}`}
          >
            <Check className="w-3 h-3" strokeWidth={3} />
          </button>
        )}
      </div>
    </td>
  );
}

// ============================================================
// Roster — Admin + Manager. Manager gets full operational access
// (Add/Edit/Deactivate/Reactivate/Freelance toggle) but the rate
// column and rate inputs are stripped per iter136 Financial Blindness.
// ============================================================
function RosterTab({ isAdmin, canEdit = false, canDelete = false }) {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await api.get("/team/staff?include_inactive=true");
    setStaff(data);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3" data-testid="roster-tab">
      <div className="flex items-center justify-between">
        <div className="text-sm text-stone-500">
          <span data-testid="roster-active-count" className="font-semibold text-stone-800">{staff.filter((s) => s.active).length}</span> active · {staff.length} total
        </div>
        {canEdit && (
          <button className="btn btn-primary btn-sm" onClick={() => setAddOpen(true)} data-testid="roster-add-btn"><Plus className="w-3.5 h-3.5" /> Add staff</button>
        )}
      </div>
      {loading ? <div className="card p-8 text-center text-stone-500 text-sm"><Loader2 className="w-4 h-4 mr-2 inline animate-spin" /> Loading…</div> : (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 text-[11px] uppercase tracking-wider text-stone-500">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">Name</th>
                {isAdmin && <th className="text-right px-3 py-2 font-semibold">Per-day rate</th>}
                <th className="text-left px-3 py-2 font-semibold">Type</th>
                {/* iter165 — Admin-only link between a User account and this
                    staff row so the staff can open their My Earnings page. */}
                {isAdmin && <th className="text-left px-3 py-2 font-semibold">User Account</th>}
                <th className="text-left px-3 py-2 font-semibold">Status</th>
                <th className="text-right px-3 py-2 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {staff.map((s) => (
                <RosterRow
                  key={s.id}
                  row={s}
                  onChanged={load}
                  isAdmin={isAdmin}
                  canEdit={canEdit}
                  canDelete={canDelete}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {addOpen && <AddStaffModal isAdmin={isAdmin} onClose={() => setAddOpen(false)} onCreated={() => { setAddOpen(false); load(); }} />}
    </div>
  );
}

function RosterRow({ row, onChanged, isAdmin, canEdit = false, canDelete = false }) {
  const [edit, setEdit] = useState(false);
  const [name, setName] = useState(row.name);
  const [rate, setRate] = useState(row.per_day_rate);
  const [sending, setSending] = useState(false);
  const save = async () => {
    // iter136 — Financial Blindness: Manager never sends `per_day_rate`.
    const patch = isAdmin ? { name, per_day_rate: Number(rate) } : { name };
    await api.patch(`/team/staff/${row.id}`, patch);
    setEdit(false);
    onChanged();
  };
  const deactivate = async () => { if (!window.confirm(`Deactivate ${row.name}?`)) return; await api.delete(`/team/staff/${row.id}`); onChanged(); };
  const reactivate = async () => { await api.patch(`/team/staff/${row.id}`, { active: true }); onChanged(); };
  // iter117 — Freelance ⇄ In-House toggle. Freelancers are hidden from the
  // Post-Prod Matrix (editors board) but stay available in Shoot Crew and
  // Calendar dropdowns.
  const isFreelance = (row.employment_type || "in_house") === "freelance";
  const toggleType = async () => {
    const next = isFreelance ? "in_house" : "freelance";
    await api.patch(`/team/staff/${row.id}`, { employment_type: next });
    onChanged();
  };
  // iter118 — Master "Send All Booked Dates" WhatsApp dispatch. Fires only
  // for freelancers (the studio's part-time crew who don't check the
  // scheduler daily). Consolidates every upcoming shoot into one message.
  const sendAllBookedDates = async () => {
    setSending(true);
    try {
      const { data } = await api.get(`/team/staff/${row.id}/schedule`);
      if (!data.wa_me_url) {
        toast.error("Could not build WhatsApp link");
        return;
      }
      window.open(data.wa_me_url, "_blank", "noopener,noreferrer");
      if (data.row_count === 0) {
        toast.info(`${row.name} has no upcoming shoots — opened WhatsApp with a friendly no-dates note.`);
      } else {
        const dateCount = new Set((data.dates || []).map((d) => d.date)).size;
        toast.success(`Opened WhatsApp with ${data.row_count} shoot${data.row_count === 1 ? "" : "s"} across ${dateCount} date${dateCount === 1 ? "" : "s"} for ${row.name}`);
      }
    } catch (e) {
      toast.error("Send failed — " + (formatApiError(e?.response?.data?.detail) || e?.message || "unknown error"));
    } finally {
      setSending(false);
    }
  };
  return (
    <tr className={row.active ? "" : "opacity-60"} data-testid={`staff-row-${row.id}`}>
      <td className="px-3 py-2">{edit ? <input className="input py-1" value={name} onChange={(e) => setName(e.target.value)} /> : <span className="font-semibold">{row.name}</span>}</td>
      {isAdmin && (
        <td className="px-3 py-2 text-right font-mono">
          {edit ? <input type="number" className="input py-1 text-right w-28 ml-auto" value={rate} onChange={(e) => setRate(e.target.value)} /> : inr(row.per_day_rate)}
        </td>
      )}
      <td className="px-3 py-2">
        <button
          type="button"
          onClick={canEdit ? toggleType : undefined}
          disabled={!canEdit}
          className={`inline-flex text-[11px] font-semibold px-2 py-0.5 rounded-full border transition-colors ${
            isFreelance
              ? "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100"
              : "bg-sky-50 text-sky-800 border-sky-200 hover:bg-sky-100"
          } ${canEdit ? "" : "cursor-default opacity-80"}`}
          title={isFreelance ? "Freelance — hidden from Post-Prod Matrix. Click to mark In-House." : "In-House — appears on Post-Prod Matrix. Click to mark Freelance."}
          data-testid={`staff-type-toggle-${row.id}`}
        >
          {isFreelance ? "Freelance" : "In-House"}
        </button>
      </td>
      {/* iter165 — Admin-only user-account linkage. */}
      {isAdmin && (
        <td className="px-3 py-2">
          <UserLinkCell staff={row} onChanged={onChanged} />
        </td>
      )}
      <td className="px-3 py-2">
        <span className={`inline-flex text-[11px] font-semibold px-2 py-0.5 rounded-full border ${row.active ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-stone-100 text-stone-500 border-stone-200"}`}>
          {row.active ? "Active" : "Inactive"}
        </span>
      </td>
      <td className="px-3 py-2 text-right">
        {edit ? (
          <span className="inline-flex gap-1">
            <button className="btn btn-primary btn-sm" onClick={save}><Save className="w-3 h-3" />Save</button>
            <button className="btn btn-outline btn-sm" onClick={() => setEdit(false)}>Cancel</button>
          </span>
        ) : (
          <span className="inline-flex gap-1">
            {/* iter118 — WhatsApp dispatch only for freelancers. In-house crew
                get schedules via the per-shoot "Send WhatsApp" button on the
                Shoot Crew card. */}
            {isFreelance && (
              <button
                type="button"
                className="btn btn-outline btn-sm inline-flex items-center gap-1 !text-emerald-700 !border-emerald-200 hover:!bg-emerald-50"
                onClick={sendAllBookedDates}
                disabled={sending}
                title="Send all booked dates via WhatsApp"
                data-testid={`staff-send-schedule-${row.id}`}
              >
                {sending ? <Loader2 className="w-3 h-3 animate-spin" /> : <MessageCircle className="w-3 h-3" strokeWidth={2} />}
                All Dates
              </button>
            )}
            {canEdit && <button className="btn btn-outline btn-sm" onClick={() => setEdit(true)} data-testid={`staff-edit-${row.id}`}>Edit</button>}
            {canDelete && (row.active ? <button className="btn btn-danger btn-sm" onClick={deactivate} data-testid={`staff-deactivate-${row.id}`}><Trash2 className="w-3 h-3" /></button> : <button className="btn btn-outline btn-sm" onClick={reactivate} data-testid={`staff-reactivate-${row.id}`}>Reactivate</button>)}
          </span>
        )}
      </td>
    </tr>
  );
}

// iter119 — Curated primary-skill dropdown for the "Add Staff" modal.
// Kept in one place so future roles can be added without touching form logic.
const PRIMARY_SKILLS = [
  "Photographer",
  "Cinematographer",
  "Drone Operator",
  "Traditional Photo",
  "Traditional Video",
  "Editor",
  "Photo Editor",
  "Video Editor",
  "Album Designer",
  "Assist",
  "Coordinator",
  "Other",
];

function AddStaffModal({ isAdmin = true, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [rate, setRate] = useState("");
  const [phone, setPhone] = useState("");
  const [primarySkill, setPrimarySkill] = useState(PRIMARY_SKILLS[0]);
  const [employmentType, setEmploymentType] = useState("in_house");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      // iter136 — Managers create staff WITHOUT touching per_day_rate.
      // Backend ignores rate for non-admin callers, but we also omit the
      // field client-side so no accidental leak of a stale value.
      const payload = {
        name: name.trim(),
        whatsapp: phone.trim(),
        primary_skill: primarySkill,
        employment_type: employmentType,
      };
      if (isAdmin) payload.per_day_rate = Number(rate);
      await api.post("/team/staff", payload);
      onCreated();
    }
    catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center px-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl w-full max-w-sm shadow-2xl" data-testid="add-staff-modal">
        <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200"><h3 className="font-heading font-semibold">Add staff</h3><button type="button" onClick={onClose}><X className="w-4 h-4" /></button></div>
        <div className="p-5 space-y-3">
          <div><label className="label">Name</label><input required autoFocus className="input" value={name} onChange={(e) => setName(e.target.value)} data-testid="add-staff-name" /></div>
          {/* iter136 — Managers don't see the rate field. Admin fills it later. */}
          {isAdmin && (
            <div>
              <label className="label">Per-day rate (₹)</label>
              <input required type="number" min="0" className="input" value={rate} onChange={(e) => setRate(e.target.value)} data-testid="add-staff-rate" />
            </div>
          )}
          {/* iter119 — Phone / Primary Skill / Employment Type. Phone persists as `whatsapp`. */}
          <div>
            <label className="label">Phone number</label>
            <input
              type="tel"
              placeholder="+91 9876543210"
              className="input"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              autoComplete="off"
              data-testid="add-staff-phone"
            />
          </div>
          <div>
            <label className="label">Primary skill / role</label>
            <select
              className="input"
              value={primarySkill}
              onChange={(e) => setPrimarySkill(e.target.value)}
              data-testid="add-staff-primary-skill"
            >
              <option value="">— Select skill —</option>
              {PRIMARY_SKILLS.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="label">Employment type</label>
            <div className="inline-flex rounded-full border border-stone-200 bg-stone-50 p-0.5" data-testid="add-staff-employment-type">
              {[
                { key: "in_house", label: "In-House", active: "bg-sky-600 text-white shadow-sm" },
                { key: "freelance", label: "Freelance", active: "bg-amber-500 text-white shadow-sm" },
              ].map((opt) => (
                <button
                  type="button"
                  key={opt.key}
                  onClick={() => setEmploymentType(opt.key)}
                  className={`text-[11px] font-semibold px-3 py-1 rounded-full transition-colors ${
                    employmentType === opt.key ? opt.active : "text-stone-600 hover:text-stone-900"
                  }`}
                  data-testid={`add-staff-employment-${opt.key}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{err}</div>}
        </div>
        <div className="px-5 py-3 border-t border-stone-100 flex justify-end gap-2">
          <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn btn-primary" disabled={busy} data-testid="add-staff-submit">Add</button>
        </div>
      </form>
    </div>
  );
}

// (PayrollTab moved to /financials — see components/WeeklyPayroll.jsx)



// ============================================================
// iter165 — Admin-only User Account linker
//
// Small, focused UI: one cell per staff row that either shows the
// linked user's name (with a Change button) or a "Link" button.
// Clicking either opens a searchable modal listing existing users.
//
// Security:
//   • The dropdown only fetches when the modal opens, and the
//     endpoint `/auth/users` is already admin-only server-side.
//   • The PATCH `/team/staff/{id}` with `user_id` is admin-only
//     (iter163) and now rejects duplicate ownership (iter165).
//
// This component intentionally does NOT create users, change
// passwords, or touch roles.
// ============================================================
function UserLinkCell({ staff, onChanged }) {
  const [open, setOpen] = useState(false);
  const [linkedUser, setLinkedUser] = useState(null);
  const linkedUserId = staff.user_id || "";

  useEffect(() => {
    let alive = true;
    if (!linkedUserId) {
      setLinkedUser(null);
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/auth/users");
        if (!alive) return;
        setLinkedUser(data.find((u) => u.id === linkedUserId) || null);
      } catch {
        if (alive) setLinkedUser(null);
      }
    })();
    return () => { alive = false; };
  }, [linkedUserId]);

  return (
    <>
      {linkedUserId ? (
        <div className="flex items-center gap-2" data-testid={`staff-user-link-${staff.id}`}>
          <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full">
            <Link2 className="w-3 h-3" />
            {linkedUser?.name || linkedUser?.email || linkedUserId.slice(0, 8)}
          </span>
          <button
            className="btn btn-outline btn-sm !py-0.5 !text-[11px]"
            onClick={() => setOpen(true)}
            data-testid={`staff-user-change-${staff.id}`}
          >
            Change
          </button>
        </div>
      ) : (
        <button
          className="btn btn-outline btn-sm !py-0.5 !text-[11px] inline-flex items-center gap-1"
          onClick={() => setOpen(true)}
          data-testid={`staff-user-link-btn-${staff.id}`}
        >
          <UserPlus className="w-3 h-3" /> Link
        </button>
      )}
      {open && (
        <LinkUserModal
          staff={staff}
          currentUserId={linkedUserId}
          onClose={() => setOpen(false)}
          onLinked={() => { setOpen(false); onChanged(); }}
        />
      )}
    </>
  );
}

function LinkUserModal({ staff, currentUserId, onClose, onLinked }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/auth/users");
        if (alive) setUsers(data || []);
      } catch (e) {
        if (alive) toast.error("Could not load users — " + (formatApiError(e?.response?.data?.detail) || e.message));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return users;
    return users.filter((u) =>
      [u.name, u.email, u.username, u.role].filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(term))
    );
  }, [users, q]);

  const link = async (u) => {
    if (saving) return;
    if (u.id === currentUserId) { onClose(); return; }
    if (currentUserId && !window.confirm(`Change linked user for "${staff.name}" to ${u.name || u.email}?`)) return;
    setSaving(true);
    try {
      await api.patch(`/team/staff/${staff.id}`, { user_id: u.id });
      toast.success(`Linked ${u.name || u.email} to ${staff.name}`);
      onLinked();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message || "Link failed");
    } finally {
      setSaving(false);
    }
  };

  const unlink = async () => {
    if (!currentUserId) return;
    if (!window.confirm(`Unlink the current user account from "${staff.name}"? Their My Earnings page will stop working until you link another account.`)) return;
    setSaving(true);
    try {
      await api.patch(`/team/staff/${staff.id}`, { user_id: "" });
      toast.success(`Unlinked from ${staff.name}`);
      onLinked();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail) || e.message || "Unlink failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="link-user-modal"
    >
      <div
        className="bg-white rounded-lg shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-4 py-3 border-b border-stone-200 flex items-center justify-between">
          <div>
            <h3 className="font-serif text-lg">Link User Account</h3>
            <p className="text-xs text-stone-500">for <span className="font-semibold text-stone-800">{staff.name}</span></p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-stone-100 rounded" data-testid="link-user-modal-close">
            <X className="w-4 h-4" />
          </button>
        </header>
        <div className="p-3 border-b border-stone-100">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-2.5 text-stone-400" />
            <input
              className="input pl-9 w-full"
              placeholder="Search by name, email or role…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              autoFocus
              data-testid="link-user-search"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto" data-testid="link-user-list">
          {loading ? (
            <div className="p-6 text-center text-stone-500 text-sm">
              <Loader2 className="w-4 h-4 inline mr-2 animate-spin" /> Loading users…
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-6 text-center text-stone-500 text-sm">No users match "{q}".</div>
          ) : (
            <ul className="divide-y divide-stone-100">
              {filtered.map((u) => {
                const isCurrent = u.id === currentUserId;
                return (
                  <li key={u.id} className="px-4 py-3 flex items-center justify-between gap-3 hover:bg-stone-50" data-testid={`link-user-row-${u.id}`}>
                    <div className="min-w-0 flex-1">
                      <div className="font-semibold text-stone-900 truncate">
                        {u.name || u.email}
                        {isCurrent && <span className="ml-2 text-[10px] uppercase tracking-wider text-emerald-700 font-bold">· Current</span>}
                      </div>
                      <div className="text-xs text-stone-500 truncate">
                        {u.email} · <span className="uppercase">{u.role}</span>
                      </div>
                    </div>
                    <button
                      className="btn btn-primary btn-sm shrink-0"
                      disabled={saving || isCurrent}
                      onClick={() => link(u)}
                      data-testid={`link-user-select-${u.id}`}
                    >
                      {isCurrent ? "Linked" : "Link"}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        {currentUserId && (
          <footer className="px-4 py-3 border-t border-stone-200 bg-stone-50 flex items-center justify-between">
            <span className="text-xs text-stone-500">Removing the link disables My Earnings for this staff.</span>
            <button
              className="btn btn-outline btn-sm text-rose-700 border-rose-200 hover:bg-rose-50"
              disabled={saving}
              onClick={unlink}
              data-testid="link-user-unlink-btn"
            >
              Unlink
            </button>
          </footer>
        )}
      </div>
    </div>
  );
}
